import React from 'react';
import {
  ClipboardPen,
  Plane,
} from 'lucide-react';
import {
  formatWingitDate,
  formatWingitTime,
} from '../dateFormat';
import wingitDesign from '../designSystem';

export default function DashboardPreviousFlightBrief({
  isMobile,
  status,
  booking,
  onCloseFlight,
  onViewFlightDetails,
}) {
  const styles = getStyles(isMobile);
  const hasBooking = Boolean(booking);
  const isEmpty = status === 'empty';
  const isClosureRequired = status === 'requires_closure';
  const isDidNotFly = status === 'did_not_fly';
  const aircraft =
    booking?.aircraft?.registration ||
    'No previous flights';
  const aircraftType =
    booking?.aircraft?.icao_type || '';
  const aircraftImage =
    booking?.aircraft?.image_url || '';
  const date = booking?.date
    ? formatWingitDate(booking.date)
    : '';
  const startTime = getFlightStartTime(booking);
  const endTime = getFlightEndTime(booking);
  const timeLabel = startTime
    ? `${formatWingitTime(startTime)}${endTime ? ` – ${formatWingitTime(endTime)}` : ''}`
    : '';
  const route = getRoute(booking);
  const statusAppearance = getStatusAppearance(status);
  const actionLabel = isClosureRequired
    ? 'Close Flight'
    : status === 'completed' || isDidNotFly
    ? 'View Flight Details'
    : '';
  const actionHandler = isClosureRequired
    ? onCloseFlight
    : status === 'completed' || isDidNotFly
    ? onViewFlightDetails
    : null;

  return (
    <section style={styles.card}>
      <div style={styles.photoPanel}>
        {hasBooking && aircraftImage ? (
          <>
            <img
              src={aircraftImage}
              alt={aircraft}
              style={styles.aircraftImage}
            />
            <div style={styles.imageShade} />
          </>
        ) : (
          <>
            <div style={styles.imageOverlay} />
            <Plane
              size={isMobile ? 34 : 46}
              strokeWidth={1.8}
              style={styles.imageIcon}
            />
          </>
        )}

        <div style={styles.photoCaption}>
          <div style={styles.imageLabel}>FLIGHT</div>
          <div style={styles.photoRegistration}>{aircraft}</div>
          {hasBooking && aircraftType ? (
            <div style={styles.imageType}>{aircraftType}</div>
          ) : null}
          {hasBooking && date ? (
            <div style={styles.imageDate}>{date}</div>
          ) : null}
        </div>
      </div>

      <div style={styles.main}>
        <div style={styles.eyebrow}>FLIGHT FOLDER</div>
        <h2 style={styles.title}>Previous Flight</h2>

        {isEmpty ? (
          <>
            <div style={styles.emptyLine}>No flights require closure</div>
            <div style={styles.emptySupportText}>
              Your most recent completed booking has been closed.
            </div>
          </>
        ) : (
          <>
            <div style={styles.metaLine}>
              <span>{date}</span>
              <span style={styles.metaDot}>•</span>
              <span>{timeLabel}</span>
            </div>

            <div style={styles.routeBlock}>
              <div style={styles.routeLabel}>ROUTE</div>
              <div style={styles.routeValue}>{route}</div>
            </div>
          </>
        )}

      </div>

      <div style={styles.statusPanel}>
        <div
          style={{
            ...styles.statusIconWrap,
            color: statusAppearance.color,
            background: statusAppearance.background,
            border: `1px solid ${statusAppearance.border}`,
          }}
        >
          <ClipboardPen size={24} />
        </div>

        <div
          style={{
            ...styles.statusBadge,
            color: statusAppearance.color,
            background: statusAppearance.background,
            border: `1px solid ${statusAppearance.border}`,
          }}
        >
          {statusAppearance.label}
        </div>
        <div style={styles.statusText}>{statusAppearance.message}</div>

        {actionHandler ? (
          <button
            type="button"
            onClick={actionHandler}
            style={styles.primaryButton}
          >
            {actionLabel}
          </button>
        ) : null}
      </div>
    </section>
  );
}

function getStatusAppearance(status) {
  if (status === 'completed') {
    return {
      label: 'COMPLETED',
      message: 'View the saved post-flight details for this flight.',
      color: '#7fd7a0',
      background: 'rgba(55,214,122,0.10)',
      border: 'rgba(55,214,122,0.24)',
    };
  }

  if (status === 'did_not_fly') {
    return {
      label: 'DID NOT FLY',
      message: 'This booking was closed without a flight taking place.',
      color: '#9fdcff',
      background: 'rgba(85,199,255,0.10)',
      border: wingitDesign.colors.border,
    };
  }

  if (status === 'empty') {
    return {
      label: 'STANDBY',
      message: 'No flights currently require closure.',
      color: wingitDesign.colors.primary,
      background: 'rgba(85,199,255,0.10)',
      border: wingitDesign.colors.border,
    };
  }

  return {
    label: 'REQUIRES CLOSURE',
    message: 'Close this flight to complete the existing post-flight workflow.',
    color: '#edc37d',
    background: 'rgba(216,164,79,0.10)',
    border: 'rgba(216,164,79,0.24)',
  };
}

function getRoute(booking) {
  const briefing =
    booking?.briefing_data &&
    typeof booking.briefing_data === 'object'
      ? booking.briefing_data
      : {};

  const departure = String(
    booking?.departure_airfield ||
      briefing.departureAirfield ||
      'Departure not set'
  ).trim();

  const destination = String(
    booking?.destination_airfield ||
      briefing.destinationAirfield ||
      'Destination not set'
  ).trim();

  return `${departure} → ${destination}`;
}

function getFlightStartTime(booking) {
  return getFlightValue(booking, [
    'time',
    'start_time',
    'startTime',
    'booking_time',
  ]);
}

function getFlightEndTime(booking) {
  return getFlightValue(booking, [
    'end_time',
    'endTime',
    'finish_time',
    'finishTime',
  ]);
}

function getFlightValue(source, keys) {
  for (const key of keys) {
    if (source?.[key]) return source[key];
  }

  return '';
}

function getStyles(isMobile) {
  const ds = wingitDesign;

  return {
    card: {
      ...ds.cards.primary,
      gridColumn: '1 / -1',
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '320px 1fr 220px',
      gap: isMobile ? '1rem' : '1.25rem',
      alignItems: 'stretch',
      padding: isMobile ? '0.9rem' : '0.85rem',
      borderRadius: isMobile ? ds.radius.large : ds.radius.xl,
      overflow: 'hidden',
    },

    photoPanel: {
      position: 'relative',
      minHeight: isMobile ? 170 : 200,
      borderRadius: ds.radius.large,
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #19304c, #07111f)',
      border: `1px solid ${ds.colors.border}`,
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
    },

    aircraftImage: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },

    imageShade: {
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(90deg, rgba(5,10,18,0.08), rgba(5,10,18,0.42))',
      zIndex: 1,
    },

    imageOverlay: {
      position: 'absolute',
      inset: 0,
      backgroundImage: `
        linear-gradient(rgba(85,199,255,0.055) 1px, transparent 1px),
        linear-gradient(90deg, rgba(85,199,255,0.045) 1px, transparent 1px)
      `,
      backgroundSize: '34px 34px',
      opacity: 0.45,
    },

    imageIcon: {
      position: 'absolute',
      right: isMobile ? 18 : 24,
      top: isMobile ? 18 : 22,
      color: 'rgba(201,241,255,0.75)',
      filter: 'drop-shadow(0 12px 22px rgba(0,0,0,0.36))',
    },

    photoCaption: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      padding: isMobile ? '1rem' : '1.1rem',
      background: 'linear-gradient(0deg, rgba(5,10,18,0.96), rgba(5,10,18,0.15))',
      zIndex: 2,
    },

    imageLabel: {
      ...ds.typography.caption,
      color: ds.colors.primary,
      fontWeight: 950,
      marginBottom: '0.35rem',
    },

    photoRegistration: {
      color: ds.colors.text,
      fontSize: isMobile ? '1.7rem' : '2rem',
      fontWeight: 950,
      letterSpacing: '-0.04em',
      lineHeight: 1,
      textShadow: '0 8px 22px rgba(0,0,0,0.55)',
    },

    imageType: {
      color: ds.colors.textMuted,
      fontSize: '0.86rem',
      fontWeight: 850,
      marginTop: '0.45rem',
    },

    imageDate: {
      color: ds.colors.textSoft,
      fontSize: '0.78rem',
      fontWeight: 850,
      marginTop: '0.55rem',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },

    main: {
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      padding: isMobile ? 0 : '0.45rem 0',
      maxWidth: 520,
    },

    eyebrow: {
      ...ds.typography.caption,
      color: ds.colors.primary,
      fontWeight: 950,
      marginBottom: '0.45rem',
    },

    title: {
      margin: 0,
      color: ds.colors.text,
      fontSize: isMobile ? '1.35rem' : '1.75rem',
      fontWeight: 900,
      letterSpacing: '-0.045em',
      lineHeight: 1,
    },

    metaLine: {
      marginTop: '0.8rem',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.55rem',
      color: ds.colors.textSoft,
      fontSize: isMobile ? '0.9rem' : '0.98rem',
      fontWeight: 800,
      flexWrap: 'wrap',
    },

    metaDot: {
      color: ds.colors.primary,
      opacity: 0.85,
    },

    routeBlock: {
      marginTop: '0.7rem',
      width: '100%',
      maxWidth: 360,
      padding: isMobile ? '0.65rem' : '0.75rem',
      borderRadius: ds.radius.large,
      background: 'rgba(255,255,255,0.035)',
      border: `1px solid ${ds.colors.border}`,
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
    },

    routeLabel: {
      ...ds.typography.caption,
      color: ds.colors.primary,
      fontWeight: 950,
      marginBottom: '0.55rem',
    },

    routeValue: {
      color: ds.colors.text,
      fontSize: isMobile ? '1rem' : '1.08rem',
      fontWeight: 900,
      letterSpacing: '-0.02em',
      lineHeight: 1.25,
      textTransform: 'uppercase',
    },

    emptyLine: {
      color: ds.colors.text,
      fontSize: '0.96rem',
      fontWeight: 750,
      lineHeight: 1.4,
      marginTop: '0.9rem',
    },

    emptySupportText: {
      color: ds.colors.textMuted,
      fontSize: '0.9rem',
      fontWeight: 650,
      lineHeight: 1.45,
      marginTop: '0.45rem',
      maxWidth: 360,
    },

    statusPanel: {
      borderLeft: isMobile ? 'none' : `1px solid ${ds.colors.border}`,
      paddingLeft: isMobile ? 0 : '0.9rem',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: isMobile ? 'flex-start' : 'center',
      textAlign: isMobile ? 'left' : 'center',
      gap: '0.55rem',
    },

    statusIconWrap: {
      width: 50,
      height: 50,
      borderRadius: 18,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    statusBadge: {
      ...ds.typography.caption,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 999,
      padding: '0.32rem 0.65rem',
      fontWeight: 950,
      letterSpacing: '0.08em',
    },

    statusText: {
      color: ds.colors.textMuted,
      fontSize: '0.88rem',
      fontWeight: 650,
      lineHeight: 1.45,
      maxWidth: 190,
    },

    primaryButton: {
      ...ds.buttons.primary,
      width: isMobile ? '100%' : 'auto',
      marginTop: '0.15rem',
    },
  };
}